// Scada_propelentes.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    Grid, Card, CardContent, Typography,
    Dialog, DialogTitle, DialogContent,
    Button, Box, Stack, Container, Chip,
    TextField, CircularProgress, Backdrop,
    Snackbar, Alert, FormControlLabel,
    Checkbox, IconButton, Tooltip
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SettingsIcon from '@mui/icons-material/Settings';
import SmsIcon from '@mui/icons-material/Sms';
import { LineChart } from '@mui/x-charts/LineChart';
import ConfigAlarmas from './ConfigAlarmas';
import ConfigSMS from './ConfigSMS';
import { UrlApi } from '../../../services/apirest';
import { fetchPermisos } from '../../../services/services'; // <-- tu helper
import Navbar from '../../Navbar';
import '@fontsource/orbitron';
import '../stkFisico/TanquesPaneles.css';
import { logEventos } from '../../../services/services';

// paleta de colores para histórico
const lineColors = [
    '#2E7D32', '#1565C0', '#C62828', '#E65100',
    '#283593', '#00695C', '#AD1457', '#FFD600',
    '#4527A0', '#00838F', '#B71C1C', '#33691E'
];

// helper de porcentaje
const pct = (v, info) => (v / info.max) * 100;

// color de la barra según nivel
const getColorForLevel = (v, info) => {
    const p = pct(v, info);
    if (p < info.alertaBajo) return '#e53935';  // rojo
    if (p > info.alertaAlto) return '#9c27b0';  // violeta
    return '#43a047';                           // verde
};

// texto siempre en negro
const textColor = () => '#000';

// nivel bajo
const isLowLevel = (v, info) => pct(v, info) < info.alertaBajo;

const Legend = () => (
    <Stack direction="row" spacing={1} justifyContent="center" mt={1}>
        <Chip label="Bajo %" size="small" sx={{ bgcolor: '#e53935', color: '#fff' }} />
        <Chip label="Normal %" size="small" sx={{ bgcolor: '#43a047', color: '#fff' }} />
        <Chip label="Sobrellenado %" size="small" sx={{ bgcolor: '#9c27b0', color: '#fff' }} />
    </Stack>
);

export default function Scada_propelentes() {
    // permisos
    const [permisos, setPermisos] = useState([]);
    const [permLoading, setPermLoading] = useState(true);

    // configuración de tanques
    const [config, setConfig] = useState([]);
    const [cfgLoading, setCfgLoading] = useState(true);
    const [cfgError, setCfgError] = useState(null);

    // datos de nivel
    const [series, setSeries] = useState([]);
    const [lastUpdate, setLastUpdate] = useState(null);

    // UI
    const [openHist, setOpenHist] = useState(false);
    const [openCfg, setOpenCfg] = useState(false);
    const [openSMS, setOpenSMS] = useState(false);
    const [selTank, setSelTank] = useState(null);
    const [desdeHist, setDesdeHist] = useState(() => {
        const d = new Date(); d.setDate(d.getDate() - 1);
        return d.toISOString().slice(0, 16);
    });
    const [hastaHist, setHastaHist] = useState(() => new Date().toISOString().slice(0, 16));
    const [visible, setVisible] = useState(Array(12).fill(true));
    const [loading, setLoading] = useState(false);
    const [snackOpen, setSnackOpen] = useState(false);
    const [snackMsg, setSnackMsg] = useState('');

    // helper local para chequear permiso puntual
    const tienePermiso = (modulo, funcion) =>
        permisos.some(p => p.modulo === modulo && p.funcion === funcion);

    // 0) cargar permisos al montar
    useEffect(() => {
        (async () => {
            try {
                const list = await fetchPermisos();
                setPermisos(list);
            } catch (err) {
                console.error('Error cargando permisos:', err);
            } finally {
                setPermLoading(false);
            }
        })();
    }, []);

    // 1) función para cargar config + niveles últimos 24h
    const loadAll = async () => {
        try {
            setCfgError(null);
            const [{ data: cfg }, { data: lvls }] = await Promise.all([
                axios.get(`${UrlApi}/scada/tanques-info`),
                axios.get(`${UrlApi}/scada/niveles`, {
                    params: {
                        desde: new Date(Date.now() - 24 * 3600 * 1000).toISOString(),
                        hasta: new Date().toISOString()
                    }
                })
            ]);
            setConfig(cfg);

            const s = cfg.map((_, i) =>
                lvls.map(r => ({
                    x: r.date_at.replace('T', ' ').substr(0, 16),
                    y: parseFloat(r[`val_tk_${i + 1}`])
                }))
            );
            setSeries(s);
            setLastUpdate(lvls.at(-1)?.date_at || null);
        } catch (err) {
            console.error('Error loadAll:', err);
            setCfgError(err);
        }
    };

    // 2) inicial + refresco c/2' (solo si no hay modales abiertos)
    useEffect(() => {
        (async () => {
            setCfgLoading(true);
            await loadAll();
            setCfgLoading(false);
        })();

        logEventos('Scada Propelentes', 'Propelentes', 'Panel SCADA', localStorage.getItem('user'));

        const id = setInterval(() => {
            if (!openHist && !openCfg && !openSMS) {
                loadAll();
            }
        }, 120000);
        return () => clearInterval(id);
    }, [openHist, openCfg, openSMS]);

    // 3) cargar histórico personalizado
    const loadHistorico = async () => {
        const d = new Date(desdeHist), h = new Date(hastaHist);
        if ((h - d) / (1000 * 3600 * 24) > 2) {
            setSnackMsg('Rango máximo 2 días');
            return setSnackOpen(true);
        }
        setLoading(true);
        try {
            const { data: lvls } = await axios.get(`${UrlApi}/scada/niveles`, {
                params: { desde: d.toISOString(), hasta: h.toISOString() }
            });
            const hs = config.map((_, i) =>
                lvls.map(r => ({
                    x: r.date_at.replace('T', ' ').substr(0, 16),
                    y: parseFloat(r[`val_tk_${i + 1}`])
                }))
            );
            setSeries(hs);
        } catch (err) {
            console.error('Error loadHistorico:', err);
            setSnackMsg('Error cargando histórico');
            setSnackOpen(true);
        } finally {
            setLoading(false);
        }
    };

    // 4) al guardar configuración: recarga niveles inmediatamente
    const handleConfigSave = newCfg => {
        setConfig(newCfg);
        (async () => {
            const { data: lvls } = await axios.get(`${UrlApi}/scada/niveles`, {
                params: {
                    desde: new Date(Date.now() - 24 * 3600 * 1000).toISOString(),
                    hasta: new Date().toISOString()
                }
            });
            const s2 = newCfg.map((_, i) =>
                lvls.map(r => ({
                    x: r.date_at.replace('T', ' ').substr(0, 16),
                    y: parseFloat(r[`val_tk_${i + 1}`])
                }))
            );
            setSeries(s2);
            setLastUpdate(lvls.at(-1)?.date_at || null);
        })();
        setOpenCfg(false);
    };

    // toggles y handlers
    const toggleVisible = i =>
        setVisible(vs => vs.map((v, idx) => idx === i ? !v : v));
    const handleCardClick = i => setSelTank(i);
    const handleCloseTank = () => setSelTank(null);

    // loader inicial o error
    if (permLoading || cfgLoading) {
        return (
            <Backdrop open sx={{ color: '#fff', zIndex: t => t.zIndex.drawer + 1 }}>
                <CircularProgress color="inherit" />
            </Backdrop>
        );
    }
    if (cfgError) {
        return (
            <Typography color="error" align="center" mt={4}>
                Error cargando configuración
            </Typography>
        );
    }

    return (
        <>
            <style>{`
        .alert-icon { animation: blink 1s infinite; }
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
      `}</style>

            <Navbar />

            <Container maxWidth="xl">
                {/* barra superior */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} mt={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        {tienePermiso('Scada_propelentes', 'configuracion') && (
                            <>
                                <Tooltip title="Configuración Alarmas">
                                    <IconButton size="small" onClick={() => setOpenCfg(true)}>
                                        <SettingsIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Configurar SMS">
                                    <IconButton size="small" onClick={() => setOpenSMS(true)}>
                                        <SmsIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                        <Typography variant="subtitle1" color="textSecondary">
                            Última lectura: {lastUpdate || '---'}
                        </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                        <Button size="small" variant="outlined" onClick={loadAll}>Actualizar</Button>
                        <Button size="small" variant="outlined" onClick={() => setOpenHist(true)}>Histórico</Button>
                    </Stack>
                </Stack>
                {console.log(config)}
                {/* tarjetas de tanques */}
                <Grid container spacing={1}>
                    {series.map((tank, i) => {
                        const info = config[i];
                        const val = tank.at(-1)?.y || 0;
                        const P = pct(val, info);
                        return (
                            <Grid item xs={12} sm={6} md={3} key={i}>
                                <Card
                                    sx={{ cursor: 'pointer', textAlign: 'center', p: 0.2, boxShadow: 4, minHeight: 200 }}
                                    onClick={() => handleCardClick(i)}
                                >
                                    <CardContent sx={{ px: 1, py: 0.5 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, p: 0.5 }}>
                                            <Typography variant="subtitle1" fontWeight={600}>{info.nombre}</Typography>
                                            {isLowLevel(val, info) && (
                                                <WarningAmberIcon className="alert-icon" sx={{ color: 'red', fontSize: 18 }} />
                                            )}
                                        </Box>
                                        <div className="tank-container">
                                            <div className="tank-shell wide">
                                                <div
                                                    className="tank-fill"
                                                    style={{
                                                        width: `${Math.min(100, P)}%`,
                                                        backgroundColor: getColorForLevel(val, info)
                                                    }}
                                                />
                                                <div className="tank-label" style={{ padding: '0 4px' }}>
                                                    <Typography sx={{ fontFamily: 'Orbitron', fontWeight: 700, fontSize: '1.6rem', color: textColor(), whiteSpace: 'nowrap' }}>
                                                        {val.toFixed(2)} m³
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontFamily: 'Orbitron', fontWeight: 400, fontSize: '1.3rem', color: textColor() }}>
                                                        {P.toFixed(0)}%
                                                    </Typography>
                                                </div>
                                            </div>
                                        </div>
                                        <Box mt={0.5}>
                                            <Grid container spacing={1}>
                                                <Grid item xs={3}>
                                                    <Typography variant="caption" color="textSecondary">100% m³</Typography>
                                                    <Typography variant="caption" fontWeight={600} display="block">
                                                        {info.max.toFixed(2)} m³
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <Typography variant="caption" color="textSecondary">85% m³</Typography>
                                                    <Typography variant="caption" fontWeight={600} display="block">
                                                        {info.tope.toFixed(2)} m³
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <Typography variant="caption" color="textSecondary">Alarmas%</Typography>
                                                    <Typography variant="caption" fontWeight={600} display="block">
                                                        {info.alertaBajo.toFixed(2)}% / {info.alertaAlto.toFixed(2)}%
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <Typography variant="caption" color="textSecondary">Producto</Typography>
                                                    <Typography variant="caption" fontWeight={600} display="block">
                                                        {info.producto}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>

                <Legend />
            </Container>

            {/* — Histórico General — */}
            <Dialog open={openHist} onClose={() => setOpenHist(false)} maxWidth="xl" fullWidth>
                <DialogTitle>Histórico — todos los tanques</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={9}>
                            <LineChart
                                width={900} height={400}
                                xAxis={[{ scaleType: 'point', data: series[0]?.map(p => p.x) }]}
                                series={series.map((d, i) => ({
                                    data: d.map(p => p.y),
                                    label: config[i].nombre,
                                    showMark: false,
                                    color: lineColors[i],
                                    visible: visible[i]
                                })).filter(s => s.visible)}
                                tooltip={{ trigger: 'axis' }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Stack spacing={2}>
                                <TextField
                                    label="Desde" type="datetime-local" value={desdeHist}
                                    onChange={e => setDesdeHist(e.target.value)}
                                    InputLabelProps={{ shrink: true }} fullWidth
                                />
                                <TextField
                                    label="Hasta" type="datetime-local" value={hastaHist}
                                    onChange={e => setHastaHist(e.target.value)}
                                    InputLabelProps={{ shrink: true }} fullWidth
                                />
                                <Button variant="outlined" onClick={loadHistorico} disabled={loading} fullWidth>
                                    {loading ? 'Buscando…' : 'Buscar'}
                                </Button>
                                {loading && <Typography variant="caption" color="textSecondary">Cargando…</Typography>}
                                <Box mt={1}>
                                    <Typography variant="subtitle2" gutterBottom>Mostrar tanques:</Typography>
                                    <Grid container>
                                        {config.map((_, i) => (
                                            <Grid item xs={6} key={i}>
                                                <FormControlLabel
                                                    control={<Checkbox checked={visible[i]} onChange={() => toggleVisible(i)} />}
                                                    label={config[i].nombre}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            </Stack>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>

            {/* — Histórico Individual — */}
            <Dialog open={selTank !== null} onClose={handleCloseTank} maxWidth="md" fullWidth>
                <DialogTitle>
                    Historial — {config[selTank]?.nombre}
                    {lastUpdate && (
                        <Typography variant="subtitle2" color="textSecondary" ml={2}>
                            Última: {lastUpdate}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    {selTank !== null && (
                        <Box mb={2}>
                            <LineChart
                                width={700} height={300}
                                xAxis={[{ scaleType: 'point', data: series[selTank].map(p => p.x) }]}
                                series={[{
                                    data: series[selTank].map(p => p.y),
                                    label: config[selTank].nombre,
                                    showMark: false
                                }]}
                            />
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackOpen} autoHideDuration={4000}
                onClose={() => setSnackOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity="warning" onClose={() => setSnackOpen(false)}>
                    {snackMsg}
                </Alert>
            </Snackbar>

            {/* Loader histórico */}
            <Backdrop open={loading} sx={{ color: '#fff', zIndex: t => t.zIndex.drawer + 1 }}>
                <CircularProgress color="inherit" />
            </Backdrop>

            {/* Modales */}
            <ConfigAlarmas open={openCfg} onClose={() => setOpenCfg(false)} onSave={handleConfigSave} />
            <ConfigSMS open={openSMS} onClose={() => setOpenSMS(false)} />
        </>
    );
}
